/**
 * useIncomingCallSocket — Robust WebSocket hook that listens for
 * incoming-call events on a per-user channel.
 *
 * Features:
 *  - Persistent WS with exponential backoff reconnect
 *  - Heartbeat ping every 25s to keep the connection alive
 *  - Tab visibility-aware: reconnects when tab becomes visible
 *  - Exposes the current incoming call (or null) and accept/decline helpers
 *  - Plays a ringtone and triggers vibrate while a call is ringing
 *  - Auto-marks calls as MISSED after MISSED_TIMEOUT_MS
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL
  ?.replace('https://', 'wss://')
  .replace('http://', 'ws://');

const MISSED_TIMEOUT_MS = 45000;
const HEARTBEAT_MS = 25000;
const MAX_BACKOFF_MS = 30000;

// Royalty-free ringtone (Pixabay). Falls back silently if blocked.
const RINGTONE_URL = 'https://cdn.pixabay.com/download/audio/2022/03/15/audio_115b9ebcb3.mp3';
const VIBRATE_PATTERN = [800, 400, 800, 400];

export default function useIncomingCallSocket({ userId, token, onCallEnded, onNotification }) {
  const [incomingCall, setIncomingCall] = useState(null);
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const ringtoneRef = useRef(null);
  const missedTimerRef = useRef(null);
  const vibrateIntervalRef = useRef(null);
  const closedManuallyRef = useRef(false);
  const tokenRef = useRef(token);
  const onCallEndedRef = useRef(onCallEnded);
  const onNotificationRef = useRef(onNotification);

  // Keep the latest callback / token in a ref so we don't churn the WS connection
  useEffect(() => { onCallEndedRef.current = onCallEnded; }, [onCallEnded]);
  useEffect(() => { onNotificationRef.current = onNotification; }, [onNotification]);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ===== Ringtone + Vibrate =====
  const startRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) {
        const a = new Audio(RINGTONE_URL);
        a.loop = true;
        a.volume = 0.7;
        ringtoneRef.current = a;
      }
      // Some browsers block autoplay; this will fail silently and we still vibrate.
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.play().catch(() => {});
    } catch {}

    // Vibrate (mobile)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate(VIBRATE_PATTERN); } catch {}
      vibrateIntervalRef.current = setInterval(() => {
        try { navigator.vibrate(VIBRATE_PATTERN); } catch {}
      }, 2400);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    try {
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }
    } catch {}
    if (vibrateIntervalRef.current) {
      clearInterval(vibrateIntervalRef.current);
      vibrateIntervalRef.current = null;
    }
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      try { navigator.vibrate(0); } catch {}
    }
  }, []);

  // ===== Missed-call timer =====
  const clearMissedTimer = useCallback(() => {
    if (missedTimerRef.current) {
      clearTimeout(missedTimerRef.current);
      missedTimerRef.current = null;
    }
  }, []);

  const armMissedTimer = useCallback((call) => {
    clearMissedTimer();
    missedTimerRef.current = setTimeout(async () => {
      // Backend will mark as missed via /timeout
      try {
        await axios.post(`${API}/calls/${call.id}/timeout`, {}, {
          headers: { Authorization: `Bearer ${tokenRef.current}` },
        });
      } catch {}
      stopRingtone();
      setIncomingCall(null);
      onCallEndedRef.current?.({ ...call, status: 'missed' });
    }, MISSED_TIMEOUT_MS);
  }, [clearMissedTimer, stopRingtone]);

  // ===== Browser notification (when tab is hidden) =====
  const showBrowserNotification = useCallback((call) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (!document.hidden) return; // tab visible — UI is enough
    try {
      const title = `📞 Incoming ${call.call_type === 'video' ? 'video' : 'voice'} call`;
      const body = `${call.caller_display_name || call.caller_username} is calling…`;
      const n = new Notification(title, {
        body,
        icon: call.caller_avatar || '/logo192.png',
        tag: `call-${call.id}`,
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {}
  }, []);

  // ===== WebSocket connection =====
  const connect = useCallback(() => {
    if (!userId || !WS_URL) return;
    if (wsRef.current && wsRef.current.readyState <= 1) return; // already connecting/open

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/${userId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        // Heartbeat
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          try {
            ws.readyState === 1 && ws.send(JSON.stringify({ type: 'ping' }));
          } catch {}
        }, HEARTBEAT_MS);
      };

      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        switch (msg.type) {
          case 'incoming_call': {
            const call = msg.data;
            setIncomingCall(call);
            startRingtone();
            armMissedTimer(call);
            showBrowserNotification(call);
            break;
          }
          case 'call_declined':
          case 'call_ended':
          case 'call_missed':
          case 'call_answered': {
            stopRingtone();
            clearMissedTimer();
            setIncomingCall(null);
            if (msg.type !== 'call_answered') onCallEndedRef.current?.(msg.data);
            break;
          }

          case 'notification': {
            onNotificationRef.current?.(msg.data);
            break;
          }
          default:
            break;
        }
      };

      ws.onerror = () => {
        // Will close right after; we'll reconnect via onclose.
      };

      ws.onclose = () => {
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        if (closedManuallyRef.current) return;
        // Exponential backoff: 1s, 2s, 4s, 8s, ... up to 30s
        const attempt = reconnectAttemptsRef.current + 1;
        reconnectAttemptsRef.current = attempt;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, attempt - 1));
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };
    } catch (e) {
      // schedule retry
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, attempt - 1));
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    }
  }, [userId, startRingtone, armMissedTimer, stopRingtone, clearMissedTimer,
      showBrowserNotification]);

  // ===== Visibility — kick a reconnect if needed =====
  useEffect(() => {
    const handler = () => {
      if (!document.hidden) {
        if (!wsRef.current || wsRef.current.readyState >= 2) connect();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [connect]);

  // ===== Lifecycle =====
  useEffect(() => {
    if (!userId) return;
    closedManuallyRef.current = false;
    // Defer initial connect slightly so React StrictMode's transient mount→unmount→mount
    // cycle doesn't close a still-CONNECTING WebSocket (which prints noisy warnings).
    const startTimer = setTimeout(() => {
      if (!closedManuallyRef.current) connect();
    }, 50);
    return () => {
      closedManuallyRef.current = true;
      clearTimeout(startTimer);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      stopRingtone();
      clearMissedTimer();
      const ws = wsRef.current;
      // Only close if the socket is already open; closing a CONNECTING WS
      // throws a benign-but-noisy warning and we'd rather rely on GC.
      if (ws && ws.readyState === 1) {
        try { ws.close(); } catch {}
      }
      wsRef.current = null;
    };
  }, [userId, connect, stopRingtone, clearMissedTimer]);

  // ===== Public actions =====
  const acceptCall = useCallback(async () => {
    if (!incomingCall) return null;
    stopRingtone();
    clearMissedTimer();
    try {
      await axios.post(`${API}/calls/${incomingCall.id}/answer`, {}, {
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
      const callData = { ...incomingCall, status: 'active', started_at: new Date().toISOString() };
      setIncomingCall(null);
      return callData;
    } catch (e) {
      setIncomingCall(null);
      return null;
    }
  }, [incomingCall, stopRingtone, clearMissedTimer]);

  const declineCall = useCallback(async () => {
    if (!incomingCall) return;
    stopRingtone();
    clearMissedTimer();
    const callId = incomingCall.id;
    setIncomingCall(null);
    try {
      await axios.post(`${API}/calls/${callId}/decline`, {}, {
        headers: { Authorization: `Bearer ${tokenRef.current}` },
      });
    } catch {}
  }, [incomingCall, stopRingtone, clearMissedTimer]);

  return { incomingCall, acceptCall, declineCall };
}
